import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";

export default function QualityManagement() {
  const { t } = useLanguage();
  return (
    <LongPage
      breadcrumb={[{ name: "Products", to: "/#modules" }]}
      eyebrow={t("Quality Management")}
      title={t("Catch defects at the source — not at the customer.")}
      seoTitle="Quality Management Software — Digital QCP, NCR, 8D — BEEDIGIT"
      seoDescription="Digital checklists, Quality Control Plans, NCR/8D, FMEA — all tied to job order, machine and batch. Stop paying for scrap and complaints. Book a demo."
      description={t("BEEDIGIT Quality puts the inspection where the work happens. Digital checklists on the operator tablet, hold/release gating, NCR/8D in one place — and full traceability from raw drum to delivered batch. Stop discovering defects in the warehouse.")}
      bullets={[
        "Digital QCP and checklists on the operator tablet",
        "Hold/release gating — bad batches don't move",
        "NCR, 8D and FMEA in one connected workflow",
        "Full traceability to job, machine, operator, batch",
      ]}
      schema="product"
      primaryCta={{ label: "Book a 30-min demo", to: "/get-started" }}
      secondaryCta={{ label: "Talk to expert", to: "/get-started" }}
      ctaHeading="Stop discovering defects on the customer's dock."
      ctaBody="Show us one product family. In 30 minutes we'll show you how every check lands on the tablet, every defect gets traced, and every complaint gets answered in minutes."
      sections={[
        {
          kind: "personas",
          eyebrow: "Who it's for",
          heading: "If your name is on the COA, this is for you",
          intro: "BEEDIGIT Quality moves inspection from clipboards and folders to one connected workflow — and keeps every batch traceable end-to-end.",
          items: [
            { role: "CEO / Owner", pain: "Customer complaints hit me before they hit anyone.", value: "Defects caught at the line, not at the loading bay." },
            { role: "COO", pain: "Quality KPIs are different in every plant.", value: "Same QCP, same NCR flow, same KPIs — every site." },
            { role: "Quality Manager", pain: "Paper checklists, missing signatures, NCRs in Outlook.", value: "Every check on a tablet. Every NCR with an owner and an SLA." },
            { role: "Plant Manager", pain: "Scrap rate is high and I can't see where it starts.", value: "Defect pareto by line, shift and operator — ranked by cost." },
            { role: "Compliance / Audit", pain: "Audits eat a week of preparation.", value: "Audit pack generated on demand. Every signature, every result, traceable." },
            { role: "CFO", pain: "COPQ is a line item we can't influence.", value: "Cost of poor quality, ranked and traceable — finally a controllable KPI." },
          ],
        },
        {
          kind: "prose",
          eyebrow: "The problem",
          heading: "Quality lives in folders. Defects live in customers' mailboxes.",
          body:
            "In most plants, the QCP exists. The checklist exists. The procedure exists. They just don't reach the operator on the day a batch is being made. Checks get signed at end of shift, from memory. NCRs sit in someone's inbox. 8D actions slip. And when the customer complaint arrives, three people spend three days reconstructing what happened.\n\nMeanwhile, scrap and rework eat your margin and your delivery dates — and most of it traces back to a missed check the system should have prevented.\n\nBEEDIGIT Quality moves the QCP onto the tablet, gates the next step on the check, and ties every result to job, machine, operator and batch — so the defect either doesn't happen, or it gets caught right there.",
        },
        {
          kind: "steps",
          eyebrow: "How it works",
          heading: "From clipboard to closed-loop in three steps",
          intro: "Your QCP becomes a workflow. Your operators sign checks on the tablet. Your back office gets clean, traceable quality data — automatically.",
          items: [
            { n: 1, title: "We digitize", body: "Your existing QCPs, sampling plans and inspection matrices become digital checklists tied to product, process and stage." },
            { n: 2, title: "Operators check", body: "BEEPAD prompts the right check at the right moment. Mandatory steps gate the next action — no skipped sign-offs." },
            { n: 3, title: "Quality closes the loop", body: "NCRs, 8Ds and FMEA updates flow in one connected workflow. Pareto by cost. Recurrence flagged. Actions tracked to closure." },
          ],
        },
        {
          kind: "features",
          eyebrow: "What you get",
          heading: "Quality at the point of work — and in the lab",
          cols: 3,
          items: [
            { title: "Digital checklists", desc: "QCPs, setup checks, in-process checks, end-of-run checks — on the BEEPAD tablet." },
            { title: "Hold / release gating", desc: "Failed checks gate the next step. Bad batches don't move." },
            { title: "Sampling plans", desc: "Statistically valid sampling — by batch, shift or production volume." },
            { title: "Lab integration", desc: "Inspection matrices, test results, COAs — captured digitally and linked to the batch." },
            { title: "NCR workflow", desc: "Non-conformance reports raised at the line, routed automatically, tracked to closure." },
            { title: "8D problem solving", desc: "Structured 8D from D0 to D8. Every action with an owner, a date and an outcome." },
            { title: "FMEA, alive", desc: "Risk analysis updated with real production data — RPN that reflects reality." },
            { title: "Defect Pareto", desc: "Top defects by line, shift, operator and product — ranked by EUR cost." },
            { title: "Full traceability", desc: "From raw drum to finished batch — forward and backward — in one query." },
          ],
        },
        {
          kind: "callout",
          eyebrow: "Live in weeks",
          heading: "Your first paperless QCP — in 14 days.",
          body: "We take your existing QCP for one product family, digitize it onto BEEPAD, train operators in under an hour, and you're catching defects at the line within two weeks. Scale from there.",
          href: "/platform",
          linkLabel: "See the platform",
        },
        {
          kind: "gains",
          eyebrow: "What it's worth",
          heading: "What every role gets back",
          intro: "Quality stops being a folder. It becomes a flow — with owners, SLAs and a cost number.",
          items: [
            { role: "CEO", cares: "Brand and customer retention.", gain: "Fewer complaints, faster answers, smaller recalls.", kpis: "Customer complaints, returns, NPS.", pitch: "Protect the brand at the line, not the lawyer's office." },
            { role: "COO", cares: "One quality standard.", gain: "Same QCP, same KPIs, every plant.", kpis: "First-pass yield, scrap %, NCR cycle time.", pitch: "Quality stops being plant-dependent." },
            { role: "Quality Manager", cares: "Inspection compliance.", gain: "Every check signed, every NCR owned, every 8D closed.", kpis: "Check compliance, NCR closure rate, 8D cycle time.", pitch: "End the paper trail. Run the standard." },
            { role: "Plant Manager", cares: "Scrap and rework.", gain: "Defect pareto by line and shift — ranked by EUR.", kpis: "Scrap %, rework hours, defect cost.", pitch: "Stop guessing where the money is leaking." },
            { role: "Compliance", cares: "Audit readiness.", gain: "Audit pack generated on demand — every result, every signature.", kpis: "Audit findings, prep time, traceability cycle time.", pitch: "Be audit-ready every day." },
            { role: "CFO", cares: "Cost of poor quality.", gain: "COPQ quantified in EUR, traceable to root cause.", kpis: "COPQ, warranty cost, return rate.", pitch: "Move a hidden cost into the light." },
            { role: "R&D / NPI", cares: "Launch quality.", gain: "FMEA fed by real production — risk priorities that reflect reality.", kpis: "RPN actuals, new-product defect rate.", pitch: "Launch with eyes open." },
          ],
        },
        {
          kind: "useCases",
          eyebrow: "In the real world",
          heading: "What changes on Monday morning",
          items: [
            { persona: "The operator", scenario: "BEEPAD prompts a setup check before the line can start. One step fails — the line can't start.", outcome: "Defect prevented before the first piece is made." },
            { persona: "The QA inspector", scenario: "Sampling plan calls for 5 pieces every 2 hours. Tablet routes the inspection automatically.", outcome: "No skipped samples. No missed batches." },
            { persona: "The shift supervisor", scenario: "An NCR is raised on Line 3. Notification hits the right phone in seconds.", outcome: "Containment in 15 minutes, not 4 hours." },
            { persona: "The QA manager", scenario: "Opens the defect pareto. The #1 cost is a recurring scratch on Product B — EUR 28k this month.", outcome: "Opens an 8D where it actually pays back." },
            { persona: "The customer", scenario: "Complains about batch FG-19877. One scan opens full genealogy — raw lot, machine, operator, every check.", outcome: "Root cause answered in 90 minutes. Customer kept." },
            { persona: "The auditor", scenario: "Asks for evidence of in-process checks for Q2. Audit pack exports in 2 minutes.", outcome: "Audit prep collapses from days to hours." },
          ],
        },
        {
          kind: "mockups",
          eyebrow: "See it",
          heading: "QCPs on the tablet. NCRs with a clock.",
          intro: "Designed for the operator at the line and the auditor in the conference room — same data, both happy.",
          items: [
            { alt: "Digital QCP checklist", caption: "The right check, at the right moment, on the right tablet. Mandatory steps gate the next action." },
            { alt: "NCR & 8D board", caption: "Every non-conformance with an owner, an SLA and a closure date. No more NCRs lost in inboxes." },
            { alt: "Defect Pareto", caption: "Top defects ranked by EUR cost. This is your improvement backlog — written by the data." },
            { alt: "Batch genealogy view", caption: "From raw drum to finished batch, forward and backward. Recall scope in minutes, not days." },
          ],
        },
        {
          kind: "kpi",
          eyebrow: "The numbers that matter",
          heading: "Four KPIs the quality team will run on",
          intro: "Live. Drillable to the batch. Exportable to your ERP, your customer and your auditor.",
          items: [
            { value: "FPY", label: "First-pass yield, live" },
            { value: "Scrap", label: "% and EUR, by cause" },
            { value: "NCR", label: "Cycle time to closure" },
            { value: "COPQ", label: "Cost of poor quality" },
          ],
        },
        {
          kind: "split",
          eyebrow: "Why BEEDIGIT Quality",
          heading: "What you have today vs what you'll have next month",
          leftTitle: "Today",
          rightTitle: "With BEEDIGIT Quality",
          left: [
            "Paper checklists signed at end of shift, from memory",
            "NCRs live in Outlook and get forgotten",
            "8D actions slip because nobody owns the clock",
            "Audit prep takes a week",
            "Defects discovered at the customer dock",
            "Recall scope answered in days — sometimes",
          ],
          right: [
            "Tablet checks at the moment of work, mandatory and gated",
            "NCRs raised at the line, routed and tracked to closure",
            "8Ds with owners, SLAs and visible progress",
            "Audit pack exported in 2 minutes",
            "Defects gated at the line — most never reach the warehouse",
            "Recall scope in 90 seconds, one QR scan",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Hardware included",
          heading: "Tablets, scanners, label printers",
          intro: "Operator tablets at every relevant station, QR scanners at every checkpoint — provisioned and ready.",
          items: [
            "BEEPAD operator tablet",
            "BEEDIGIT HUB",
            "Handheld QR scanner",
            "Label printer kit",
            "GSM · Wi-Fi · Ethernet",
            "Encrypted MQTT over TLS",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Plays well with your stack",
          heading: "Your ERP, your QMS, your BI",
          intro: "Push and pull quality data with the systems your business already runs on.",
          items: [
            "factory.online", "SAP", "Oracle EBS / Fusion", "Microsoft Dynamics", "Odoo",
            "Power BI", "Tableau", "Data lakes (CSV / JSON / REST)",
            "MQTT", "OPC UA", "REST / Webhooks",
          ],
        },
        {
          kind: "split",
          eyebrow: "Your rollout",
          heading: "From paper to gated in 90 days",
          leftTitle: "Weeks 1–4",
          rightTitle: "Weeks 5–12",
          left: [
            "Digitize the first QCP for one product family",
            "Tablets mounted, operators trained in under an hour",
            "NCR workflow live — first NCRs raised at the line",
            "First defect pareto by EUR — and your first \"we had no idea\" moment",
          ],
          right: [
            "Roll out QCPs across remaining product families",
            "8D and FMEA workflows tuned with your quality team",
            "Audit pack generation enabled — and tested in a mock audit",
            "COPQ tracked monthly — to the board, not the binder",
          ],
        },
        {
          kind: "callout",
          eyebrow: "Officially certified",
          heading: "The only MES certified by INCIT for raising your SIRI index",
          body: "Quality processes score in SIRI. BEEDIGIT is the first platform officially recognized for moving the needle on the Smart Industry Readiness Index.",
          href: "/siri",
          linkLabel: "See how BEEDIGIT raises SIRI",
        },
        {
          kind: "faq",
          eyebrow: "FAQ",
          heading: "What buyers ask us first",
          items: [
            { q: "Do we have to rewrite our QCPs?", a: "No. We take your existing QCPs, sampling plans and inspection matrices and digitize them as-is. You can refine them later — start by running what you already have." },
            { q: "How long until the first paperless QCP?", a: "Typically 2 weeks from kickoff to the first QCP running on tablets at the line. Full rollout across product families follows in 60–90 days." },
            { q: "Will it stop bad batches from moving?", a: "Yes — that's the point of hold/release gating. A failed mandatory check blocks the next operation until quality clears it. No more bad batches sneaking through." },
            { q: "How does this integrate with our lab / LIMS?", a: "Inspection matrices and test results can be entered on BEEPAD or pulled from your LIMS via REST/CSV. Either way, results land against the same batch record." },
            { q: "Will it work for regulated industries (food, pharma, automotive)?", a: "Yes. Full audit trail, electronic signatures, role-based access, time-stamped results. Customers run BEEDIGIT in ISO 9001, IATF 16949 and HACCP environments." },
            { q: "Will our operators actually use it?", a: "Yes — designed for them, not for IT. Tablet interface closer to a phone than a SCADA screen. Training under an hour. Adoption above 90% in the first month." },
            { q: "Will it connect to our ERP and BI?", a: "Out of the box — SAP, Oracle, Microsoft Dynamics, Odoo, Power BI, Tableau. Bi-directional REST/JSON, plus MQTT and OPC UA." },
            { q: "What does it cost?", a: "Priced per connected work center, hardware included. Most customers see payback inside two quarters from scrap and complaint reduction. Book the demo — we'll build the business case." },
          ],
        },
        {
          kind: "related",
          eyebrow: "Explore the platform",
          heading: "Pairs naturally with",
          items: [
            { title: "Manufacturing Execution", href: "/products/manufacturing-execution", desc: "Job orders, confirmations and QR — the production context every check lands against." },
            { title: "Monitoring & OEE", href: "/products/monitoring-oee", desc: "Live OEE per machine — see the speed and downtime behind every defect spike." },
            { title: "CMMS", href: "/products/cmms", desc: "Tie recurring defects to the asset that causes them. Maintain what's hurting quality." },
            { title: "Workspace", href: "/products/workspace", desc: "Andon, issues and handover — close the loop on every NCR." },
          ],
        },
      ]}
    />
  );
}